import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, CircularProgress, Alert,
  Dialog, DialogActions, DialogContent, DialogTitle, TextField, Grid,
  Select, MenuItem, FormControl, InputLabel, Checkbox, FormControlLabel,
  Tabs, Tab, Box as TabPanelBox, Chip,
  Input // For file input
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, UploadFile as UploadFileIcon, AutoFixHigh as AutoFixHighIcon, Warning as WarningIcon, Download as DownloadIcon } from '@mui/icons-material';
import api from '../../services/api';
import QuestionEditorModal from './QuestionEditorModal'; // Import the new modal

const ANSWER_TYPES = [
  'radio_likert_0_3', 'radio_likert_1_5', 'radio_custom', 
  'checkbox_custom', 'number_input', 'text_input_short', 'text_input_long'
];

const CALCULATION_TYPES = ['sum_answer_values']; // Extend as needed

const initialQuestionState = {
  questionId: '', text: '', answerType: 'radio_likert_0_3', 
  answerOptions: [{ label: '', value: '' }], required: true, critical: false
};

const initialTestDefinitionState = {
  testKey: '', fullName: '', description: '', version: '1.0',
  questions: [initialQuestionState],
  detailedScoring: { 
    minTotalScore: 0, 
    maxTotalScore: 0, 
    primaryInterpretationText: '', 
    subscales: [], 
    interpretiveRules: [],
    rawImportedScoring: {} 
  },
  defaultPresentationMode: 'single_page',
  allowReviewBeforeSubmit: true,
  submissionConfirmationMessage: 'Your assessment has been submitted successfully.',
  isEnabled: true
};

const AssessmentDefinitionManager = ({ token }) => {
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [currentDefinition, setCurrentDefinition] = useState(null);
  const [formData, setFormData] = useState(initialTestDefinitionState);
  const [formErrors, setFormErrors] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
  const [editingQuestionData, setEditingQuestionData] = useState(null);

  // Helper function to generate the technical name preview
  const generateTechnicalNamePreview = (testKey) => {
    if (!testKey) return { name: '', length: 0, isValid: true };

    const prefix = 'administer_';
    const suffix = '_assessment';
    const techName = testKey.replace(/-/g, '_');
    const fullName = `${prefix}${techName}${suffix}`;

    return {
      name: fullName,
      length: fullName.length,
      isValid: fullName.length <= 64,
      willBeTruncated: fullName.length > 64
    };
  }; 

  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');

  const [generatingToolFor, setGeneratingToolFor] = useState(null); 
  const [generateToolError, setGenerateToolError] = useState('');
  const [generateToolSuccess, setGenerateToolSuccess] = useState('');
  const [assessmentToolMap, setAssessmentToolMap] = useState({});


  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    fetchDefinitionsAndTools();
  }, [token]);

  const fetchDefinitionsAndTools = async () => {
    setLoading(true);
    try {
      const [definitionsResponse, toolsResponse] = await Promise.all([
        api.adminListTestDefinitions(token),
        api.adminListTools(token) 
      ]);
      
      setDefinitions(definitionsResponse.data || []);

      const toolMap = {};
      if (toolsResponse.data && Array.isArray(toolsResponse.data)) {
        toolsResponse.data.forEach(tool => {
          if (tool.category === 'assessment' && 
              tool.technicalSpec?.parameters?.properties?.testKey?.enum?.[0]) {
            toolMap[tool.technicalSpec.parameters.properties.testKey.enum[0]] = true;
          }
        });
      }
      setAssessmentToolMap(toolMap);

    } catch (err) {
      console.error("Error fetching definitions or tools:", err);
      setError(err.response?.data?.msg || 'Failed to load initial data');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchDefinitions = async () => { // Keep original fetchDefinitions if called separately elsewhere
    setLoading(true);
    try {
      const response = await api.adminListTestDefinitions(token);
      setDefinitions(response.data || []);
    } catch (err) {
      console.error("Error fetching test definitions:", err);
      setError(err.response?.data?.msg || 'Failed to load test definitions');
    } finally {
      setLoading(false);
    }
  };


  const handleOpenDialog = (definition = null) => {
    setCurrentDefinition(definition);
    if (definition) {
      // Ensure detailedScoring is initialized if not present in the definition
      const detailedScoringData = definition.detailedScoring && Object.keys(definition.detailedScoring).length > 0
        ? definition.detailedScoring
        : initialTestDefinitionState.detailedScoring;

      const populatedDefinition = {
        ...initialTestDefinitionState, // Start with defaults (which now includes detailedScoring structure)
        ...definition,                 // Overlay with actual definition data
        questions: definition.questions?.length 
          ? definition.questions.map(q => ({...initialQuestionState, ...q, answerOptions: q.answerOptions?.length ? q.answerOptions : [{label:'', value:''}] })) 
          : [initialQuestionState],
        detailedScoring: detailedScoringData, // Ensure detailedScoring is correctly populated
      };
      // Remove legacy fields explicitly if they somehow sneak in from `...definition`
      delete populatedDefinition.scoringRules;
      delete populatedDefinition.interpretationGuidelines;
      
      setFormData(populatedDefinition);
    } else {
      setFormData(initialTestDefinitionState);
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentDefinition(null);
    setActiveTab(0);
  };

  const handleOpenQuestionModal = (question = null, index = -1) => {
    const questionToEdit = question ? JSON.parse(JSON.stringify(question)) : initialQuestionState;
    setEditingQuestionData({ question: questionToEdit, index });
    setIsQuestionModalOpen(true);
  };

  const handleCloseQuestionModal = () => {
    setIsQuestionModalOpen(false);
    setEditingQuestionData(null);
  };

  const handleSaveQuestion = (updatedQuestion, index) => {
    setFormData(prev => {
      const newQuestions = [...prev.questions];
      if (index > -1) {
        newQuestions[index] = updatedQuestion;
      } else {
        newQuestions.push(updatedQuestion);
      }
      return { ...prev, questions: newQuestions };
    });
    handleCloseQuestionModal();
  };

  const handleInputChange = (e, path = null, index = null, subPath = null, subIndex = null) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      let newClonedData = JSON.parse(JSON.stringify(prev)); 

      if (path && index !== null && subPath && subIndex !== null) {
        if (!newClonedData[path]) newClonedData[path] = [];
        while (newClonedData[path].length <= index) newClonedData[path].push({});
        if (!newClonedData[path][index][subPath]) newClonedData[path][index][subPath] = [];
        while (newClonedData[path][index][subPath].length <= subIndex) newClonedData[path][index][subPath].push({});
        newClonedData[path][index][subPath][subIndex] = { ...newClonedData[path][index][subPath][subIndex], [name]: val };
      } else if (path && index !== null && subPath) {
        if (!newClonedData[path]) newClonedData[path] = [];
        while (newClonedData[path].length <= index) newClonedData[path].push({});
        if (!newClonedData[path][index][subPath]) newClonedData[path][index][subPath] = {};
        newClonedData[path][index][subPath] = { ...newClonedData[path][index][subPath], [name]: val };
      } else if (path && index !== null) {
        if (!newClonedData[path]) newClonedData[path] = [];
        while (newClonedData[path].length <= index) newClonedData[path].push({});
        newClonedData[path][index] = { ...newClonedData[path][index], [name]: val };
      } else if (path && index === null) {
        // Handle nested object properties (like detailedScoring.primaryInterpretationText)
        if (!newClonedData[path]) newClonedData[path] = {};
        newClonedData[path][name] = val;
      } else {
        newClonedData[name] = val;
      }
      return newClonedData;
    });
  };
  
  const addArrayItem = (path, subPath = null, index = null, itemStructure) => {
    setFormData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      if (subPath && index !== null) {
        if (!newData[path][index][subPath]) newData[path][index][subPath] = [];
        newData[path][index][subPath].push(itemStructure);
      } else {
        if (!newData[path]) newData[path] = [];
        newData[path].push(itemStructure);
      }
      return newData;
    });
  };

  const removeArrayItem = (path, itemIndex, subPath = null, parentIndex = null) => {
    setFormData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      if (subPath && parentIndex !== null) {
        newData[path][parentIndex][subPath].splice(itemIndex, 1);
      } else {
        newData[path].splice(itemIndex, 1);
      }
      return newData;
    });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.testKey?.trim()) errors.testKey = 'Test Key is required.';
    if (!formData.fullName?.trim()) errors.fullName = 'Full Name is required.';
    if (!formData.description?.trim()) errors.description = 'Description is required.';
    formData.questions?.forEach((q, i) => {
      if (!q.questionId?.trim()) errors[`question_${i}_questionId`] = `Question ID for question ${i+1} is required.`;
      if (!q.text?.trim()) errors[`question_${i}_text`] = `Text for question ${i+1} is required.`;
      if ((q.answerType === 'radio_custom' || q.answerType === 'checkbox_custom') && (!q.answerOptions || q.answerOptions.length === 0 || q.answerOptions.some(opt => !opt.label?.trim() || opt.value === ''))) {
        errors[`question_${i}_answerOptions`] = `Answer options for question ${i+1} are required and must be complete.`;
      }
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      if (currentDefinition) {
        await api.adminUpdateTestDefinition(currentDefinition._id, formData, token);
        setSuccess('Test definition updated successfully.');
      } else {
        await api.adminCreateTestDefinition(formData, token);
        setSuccess('Test definition created successfully.');
      }
      fetchDefinitionsAndTools(); // Refresh both definitions and tool map
      handleCloseDialog();
    } catch (err) {
      console.error("Error saving test definition:", err);
      setError(err.response?.data?.msg || 'Failed to save test definition.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to disable this test definition?')) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await api.adminDeleteTestDefinition(id, token);
      setSuccess('Test definition disabled successfully.');
      fetchDefinitionsAndTools(); // Refresh both
    } catch (err) {
      console.error("Error disabling test definition:", err);
      setError(err.response?.data?.msg || 'Failed to disable test definition.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateTool = async (testKey) => {
    if (!window.confirm(`Are you sure you want to attempt to generate an AI tool for '${testKey}'? This will call an LLM and may incur costs.`)) return;
    setGeneratingToolFor(testKey);
    setGenerateToolError('');
    setGenerateToolSuccess('');
    try {
      const response = await api.adminGenerateToolForAssessment(testKey, token);
      setGenerateToolSuccess(`Tool for '${testKey}' generated successfully: ${response.data?.name}`);
      fetchDefinitionsAndTools(); // Refresh tool map
    } catch (err) {
      console.error(`Error generating tool for ${testKey}:`, err);
      setGenerateToolError(err.response?.data?.msg || `Failed to generate tool for ${testKey}.`);
    } finally {
      setGeneratingToolFor(null);
    }
  };

  const handleExportDefinition = async (testKey) => {
    try {
      const response = await api.adminExportTestDefinition(testKey, token);
      // Create a blob and download it
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${testKey}-export.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSuccess(`Exported '${testKey}' successfully.`);
    } catch (err) {
      console.error(`Error exporting ${testKey}:`, err);
      setError(err.response?.data?.msg || `Failed to export ${testKey}.`);
    }
  };

  if (loading && definitions.length === 0) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Paper sx={{ p: 2, m: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Manage Assessment Test Definitions</Typography>
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<UploadFileIcon />} 
            onClick={() => setOpenImportDialog(true)}
            sx={{ mr: 1 }}
          >
            Import from JSON
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Add New Definition
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}
      {importError && <Alert severity="error" onClose={() => setImportError('')} sx={{ mb: 2 }}>{importError}</Alert>}
      {importSuccess && <Alert severity="success" onClose={() => setImportSuccess('')} sx={{ mb: 2 }}>{importSuccess}</Alert>}
      {generateToolError && <Alert severity="error" onClose={() => setGenerateToolError('')} sx={{ mb: 2 }}>{generateToolError}</Alert>}
      {generateToolSuccess && <Alert severity="success" onClose={() => setGenerateToolSuccess('')} sx={{ mb: 2 }}>{generateToolSuccess}</Alert>}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Test Key</TableCell>
              <TableCell>Full Name</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Enabled</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {definitions.map((def) => (
              <TableRow key={def._id}>
                <TableCell>{def.testKey}</TableCell>
                <TableCell>{def.fullName}</TableCell>
                <TableCell>{def.version}</TableCell>
                <TableCell>{def.isEnabled ? 'Yes' : 'No'}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpenDialog(def)} title="Edit Definition"><EditIcon /></IconButton>
                  <IconButton onClick={() => handleExportDefinition(def.testKey)} title="Export as JSON"><DownloadIcon /></IconButton>
                  {def.isEnabled && <IconButton onClick={() => handleDelete(def._id)} title="Disable Definition"><DeleteIcon /></IconButton>}
                  <IconButton
                    onClick={() => handleGenerateTool(def.testKey)}
                    disabled={generatingToolFor === def.testKey || assessmentToolMap[def.testKey]}
                    title={assessmentToolMap[def.testKey] ? `Tool already exists for ${def.testKey}` : `Generate AI Tool for ${def.testKey}`}
                  >
                    {generatingToolFor === def.testKey ? <CircularProgress size={24} /> : <AutoFixHighIcon color={assessmentToolMap[def.testKey] ? "disabled" : "primary"} />}
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>{currentDefinition ? 'Edit' : 'Add'} Test Definition</DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="assessment definition tabs">
              <Tab label="Test Details" id="tab-details" aria-controls="tabpanel-details" />
              <Tab label="Questions" id="tab-questions" aria-controls="tabpanel-questions" />
              <Tab label="Scoring & Interpretation" id="tab-scoring" aria-controls="tabpanel-scoring" />
            </Tabs>
          </Box>

          <TabPanelBox role="tabpanel" hidden={activeTab !== 0} id="tabpanel-details" aria-labelledby="tab-details" sx={{ pt: 2 }}>
            {activeTab === 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Test Key"
                    name="testKey"
                    value={formData.testKey}
                    onChange={(e) => handleInputChange(e)}
                    error={!!formErrors.testKey}
                    helperText={formErrors.testKey}
                  />
                  {formData.testKey && (() => {
                    const preview = generateTechnicalNamePreview(formData.testKey);
                    return (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color={preview.isValid ? "text.secondary" : "error"}>
                          Technical name ({preview.length}/64 chars):
                        </Typography>
                        {preview.willBeTruncated && (
                          <Chip
                            size="small"
                            icon={<WarningIcon />}
                            label="Will be truncated"
                            color="warning"
                            sx={{ ml: 1, height: 20 }}
                          />
                        )}
                        <Typography
                          variant="caption"
                          display="block"
                          color={preview.isValid ? "text.secondary" : "error"}
                          sx={{
                            wordBreak: 'break-all',
                            fontFamily: 'monospace',
                            fontSize: '0.7rem'
                          }}
                        >
                          {preview.name}
                        </Typography>
                      </Box>
                    );
                  })()}
                </Grid>
                <Grid item xs={12} sm={6} md={5}><TextField fullWidth label="Full Name" name="fullName" value={formData.fullName} onChange={(e) => handleInputChange(e)} error={!!formErrors.fullName} helperText={formErrors.fullName} /></Grid>
                <Grid item xs={12} sm={6} md={4}><TextField fullWidth label="Description" name="description" value={formData.description} onChange={(e) => handleInputChange(e)} error={!!formErrors.description} helperText={formErrors.description} /></Grid>
                <Grid item xs={12} sm={6} md={3}><TextField fullWidth label="Version" name="version" value={formData.version} onChange={(e) => handleInputChange(e)} /></Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Default Presentation</InputLabel>
                    <Select name="defaultPresentationMode" value={formData.defaultPresentationMode} label="Default Presentation" onChange={(e) => handleInputChange(e)}>
                      <MenuItem value="single_page">Single Page</MenuItem>
                      <MenuItem value="one_at_a_time">One at a Time</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}><FormControlLabel control={<Checkbox checked={formData.allowReviewBeforeSubmit} onChange={(e) => handleInputChange(e)} name="allowReviewBeforeSubmit" />} label="Allow Review Before Submit" /></Grid>
                <Grid item xs={12} sm={6} md={3}><FormControlLabel control={<Checkbox checked={formData.isEnabled} onChange={(e) => handleInputChange(e)} name="isEnabled" />} label="Is Enabled" /></Grid>
                <Grid item xs={12}><TextField fullWidth label="Submission Confirmation Message" name="submissionConfirmationMessage" value={formData.submissionConfirmationMessage} onChange={(e) => handleInputChange(e)} /></Grid>
              </Grid>
            )}
          </TabPanelBox>

          <TabPanelBox role="tabpanel" hidden={activeTab !== 1} id="tabpanel-questions" aria-labelledby="tab-questions" sx={{ pt: 2 }}>
            {activeTab === 1 && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={() => handleOpenQuestionModal(null, -1)}>
                    Add New Question
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  {formData.questions.map((q, qIndex) => (
                    <Box key={`q-summary-${qIndex}`}
                          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p:1, border: '1px solid #e0e0e0', borderRadius: '4px', mb:1, width: '100%' }}>
                      <Box>
                          <Typography variant="subtitle1" component="div">Question {qIndex + 1}: {q.questionId || `(New Question ${qIndex+1})`}</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ml:1}}>{q.text ? q.text.substring(0, 70) + (q.text.length > 70 ? '...' : '') : '(No text yet)'}</Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ml:1}}>Type: {q.answerType}</Typography>
                      </Box>
                      <Box>
                        <IconButton size="small" onClick={() => handleOpenQuestionModal(q, qIndex)}><EditIcon /></IconButton>
                        <IconButton size="small" color="error" onClick={() => removeArrayItem('questions', qIndex)} disabled={formData.questions.length <= 1}><DeleteIcon /></IconButton>
                      </Box>
                    </Box>
                  ))}
                </Grid>
              </Grid>
            )}
          </TabPanelBox>

          <TabPanelBox role="tabpanel" hidden={activeTab !== 2} id="tabpanel-scoring" aria-labelledby="tab-scoring" sx={{ pt: 2 }}>
            {activeTab === 2 && formData.detailedScoring && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>Overall Score Settings</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Minimum Total Score"
                        name="minTotalScore"
                        value={formData.detailedScoring.minTotalScore || ''}
                        onChange={(e) => handleInputChange(e, 'detailedScoring')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Maximum Total Score"
                        name="maxTotalScore"
                        value={formData.detailedScoring.maxTotalScore || ''}
                        onChange={(e) => handleInputChange(e, 'detailedScoring')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Primary Interpretation Text (Overall)"
                        name="primaryInterpretationText"
                        value={formData.detailedScoring.primaryInterpretationText || ''}
                        onChange={(e) => handleInputChange(e, 'detailedScoring')}
                        multiline
                        minRows={2}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Subscales Section */}
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6">Subscales</Typography>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => addArrayItem('detailedScoring', 'subscales', null, { id: '', name: '', item_ids: [], score_range: { min: 0, max: 0 }, interpretationRules: [] })}
                    >
                      Add Subscale
                    </Button>
                  </Box>
                  {formData.detailedScoring.subscales?.map((subscale, sIndex) => (
                    <Paper key={`subscale-${sIndex}`} variant="outlined" sx={{ p: 2, mb: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>Subscale {sIndex + 1}</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <TextField fullWidth label="Subscale ID" name="id" value={subscale.id} onChange={(e) => handleInputChange(e, 'detailedScoring', 'subscales', sIndex)} />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField fullWidth label="Subscale Name" name="name" value={subscale.name} onChange={(e) => handleInputChange(e, 'detailedScoring', 'subscales', sIndex)} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Item IDs (comma-separated)"
                            name="item_ids_str" // Use a temporary string field for input
                            value={Array.isArray(subscale.item_ids) ? subscale.item_ids.join(',') : ''}
                            onChange={(e) => {
                              const newIds = e.target.value.split(',').map(id => id.trim()).filter(id => id);
                              // Create a synthetic event-like object for handleInputChange
                              const syntheticEvent = { target: { name: 'item_ids', value: newIds }};
                              handleInputChange(syntheticEvent, 'detailedScoring', 'subscales', sIndex);
                            }}
                            helperText="e.g., question_1,question_2"
                          />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField type="number" fullWidth label="Min Score (Subscale)" name="min" value={subscale.score_range?.min || ''} onChange={(e) => handleInputChange(e, 'detailedScoring', 'subscales', sIndex, 'score_range')} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField type="number" fullWidth label="Max Score (Subscale)" name="max" value={subscale.score_range?.max || ''} onChange={(e) => handleInputChange(e, 'detailedScoring', 'subscales', sIndex, 'score_range')} />
                        </Grid>
                        <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-start' }}>
                            <Button 
                                color="error" 
                                onClick={() => removeArrayItem('detailedScoring', sIndex, 'subscales')}
                                startIcon={<DeleteIcon />}
                                sx={{mb:1}}
                            >
                                Remove Subscale
                            </Button>
                        </Grid>
                        {/* Placeholder for subscale.interpretationRules editor */}
                        <Grid item xs={12}>
                            <Typography variant="caption" sx={{ml:1}}>Subscale Interpretation Rules editor coming soon.</Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Grid>

                {/* Interpretive Rules Section */}
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6">Interpretive Rules (Overall & Subscale)</Typography>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => addArrayItem('detailedScoring', 'interpretiveRules', null, { id: '', name: '', appliesTo: 'total_score', condition: { operator: '>=', value: 0 }, interpretation: '', isCriticalAlert: false })}
                    >
                      Add Interpretive Rule
                    </Button>
                  </Box>
                  {formData.detailedScoring.interpretiveRules?.map((rule, irIndex) => (
                    <Paper key={`irule-${irIndex}`} variant="outlined" sx={{ p: 2, mb: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>Interpretive Rule {irIndex + 1}</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <TextField fullWidth label="Rule ID" name="id" value={rule.id} onChange={(e) => handleInputChange(e, 'detailedScoring', 'interpretiveRules', irIndex)} />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                          <TextField fullWidth label="Rule Name" name="name" value={rule.name} onChange={(e) => handleInputChange(e, 'detailedScoring', 'interpretiveRules', irIndex)} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControl fullWidth>
                            <InputLabel>Applies To</InputLabel>
                            <Select name="appliesTo" value={rule.appliesTo} label="Applies To" onChange={(e) => handleInputChange(e, 'detailedScoring', 'interpretiveRules', irIndex)}>
                              <MenuItem value="total_score">Total Score</MenuItem>
                              <MenuItem value="subscale_score">Subscale Score</MenuItem>
                              <MenuItem value="item_count">Item count (items at their own minimum)</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        {rule.appliesTo === 'item_count' && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              Counts items reaching their minimum: {(rule.itemThresholds || []).map(t => `${t.questionId} ≥ ${t.minValue}`).join(', ') || 'none set'}. The condition below applies to that count. Item minimums are set by migration script, not here.
                            </Typography>
                          </Grid>
                        )}
                        {rule.appliesTo === 'subscale_score' && (
                          <Grid item xs={12} sm={4}>
                            <TextField fullWidth label="Subscale ID Ref" name="subscaleIdRef" value={rule.subscaleIdRef || ''} onChange={(e) => handleInputChange(e, 'detailedScoring', 'interpretiveRules', irIndex)} />
                          </Grid>
                        )}
                        <Grid item xs={12} sm={rule.appliesTo === 'subscale_score' ? 4 : 6}>
                          <FormControl fullWidth>
                            <InputLabel>Condition Operator</InputLabel>
                            <Select name="operator" value={rule.condition?.operator} label="Condition Operator" onChange={(e) => handleInputChange(e, 'detailedScoring', 'interpretiveRules', irIndex, 'condition')}>
                              {['>=', '<=', '>', '<', '==', 'between'].map(op => <MenuItem key={op} value={op}>{op}</MenuItem>)}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={rule.appliesTo === 'subscale_score' ? 4 : 6}>
                          <TextField 
                            fullWidth 
                            label="Condition Value" 
                            name="value" 
                            value={ (rule.condition?.operator === 'between' && typeof rule.condition?.value === 'object') ? `${rule.condition.value.min},${rule.condition.value.max}` : (rule.condition?.value || '') }
                            onChange={(e) => {
                                const val = e.target.value;
                                let processedVal;
                                if (rule.condition?.operator === 'between') {
                                    const parts = val.split(',');
                                    processedVal = { min: parseFloat(parts[0]) || 0, max: parseFloat(parts[1]) || 0 };
                                } else {
                                    processedVal = parseFloat(val) || val; // Keep as string if not parsable to float, or just use val
                                }
                                const syntheticEvent = { target: { name: 'value', value: processedVal }};
                                handleInputChange(syntheticEvent, 'detailedScoring', 'interpretiveRules', irIndex, 'condition');
                            }}
                            helperText={rule.condition?.operator === 'between' ? "Enter as min,max (e.g., 5,9)" : "Enter number"}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField fullWidth label="Interpretation Text" name="interpretation" value={rule.interpretation} onChange={(e) => handleInputChange(e, 'detailedScoring', 'interpretiveRules', irIndex)} multiline minRows={2} />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel control={<Checkbox checked={rule.isCriticalAlert || false} name="isCriticalAlert" onChange={(e) => handleInputChange(e, 'detailedScoring', 'interpretiveRules', irIndex)} />} label="Is Critical Alert" />
                        </Grid>
                        <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                           <Button 
                                color="error" 
                                onClick={() => removeArrayItem('detailedScoring', irIndex, 'interpretiveRules')}
                                startIcon={<DeleteIcon />}
                            >
                                Remove Rule
                            </Button>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Grid>
              </Grid>
            )}
          </TabPanelBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : (currentDefinition ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {isQuestionModalOpen && (
        <QuestionEditorModal
          open={isQuestionModalOpen}
          onClose={handleCloseQuestionModal}
          questionData={editingQuestionData?.question}
          questionIndex={editingQuestionData?.index}
          onSave={handleSaveQuestion}
          existingQuestionIds={formData.questions
            .map(q => q.questionId)
            .filter(id => id !== editingQuestionData?.question?.questionId) 
          }
        />
      )}

      <Dialog open={openImportDialog} onClose={() => setOpenImportDialog(false)}>
        <DialogTitle>Import Assessment from JSON</DialogTitle>
        <DialogContent>
          <Input
            type="file"
            inputProps={{ accept: '.json' }}
            onChange={(e) => setSelectedFile(e.target.files[0])}
            fullWidth
            sx={{ my: 2 }}
          />
          {importError && <Alert severity="error" sx={{mt:1}}>{importError}</Alert>}
          {importSuccess && <Alert severity="success" sx={{mt:1}}>{importSuccess}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenImportDialog(false); setSelectedFile(null); setImportError(''); setImportSuccess(''); }}>Cancel</Button>
          <Button 
            onClick={async () => {
              if (!selectedFile) {
                setImportError('Please select a JSON file to import.');
                return;
              }
              setImportLoading(true);
              setImportError('');
              setImportSuccess('');
              const formData = new FormData();
              formData.append('assessmentFile', selectedFile);
              try {
                await api.adminImportTestDefinition(formData, token);
                setImportSuccess(`Successfully imported ${selectedFile.name}.`);
                setSelectedFile(null);
                fetchDefinitionsAndTools(); // Refresh both definitions and tool map
              } catch (err) {
                console.error("Error importing test definition:", err);
                setImportError(err.response?.data?.msg || err.message || 'Failed to import test definition.');
              } finally {
                setImportLoading(false);
              }
            }} 
            variant="contained" 
            disabled={importLoading || !selectedFile}
          >
            {importLoading ? <CircularProgress size={24} /> : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

    </Paper>
  );
};

export default AssessmentDefinitionManager;
